import {Icon} from '@benchling/nucleus-ui';
import {Alert, Text} from '@mantine/core';
import gql from 'graphql-tag';
import React, {useEffect, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import SourceDAO from 'file-browser/SourceDAO';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import {evictReviewLifecyclesFromCacheForCollections} from 'notebook/entry-templates/collections/TemplateCollectionReviewProcessUtils';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {
  OrganizationReviewLifecyclesQuery,
  OrganizationReviewLifecyclesQueryVariables,
} from './__generated/OrganizationReviewLifecyclesQuery';
import {
  ProjectOrganizationsQuery,
  ProjectOrganizationsQueryVariables,
} from './__generated/ProjectOrganizationsQuery';
import {
  TemplateCollectionOrganizationsQuery,
  TemplateCollectionOrganizationsQueryVariables,
} from './__generated/TemplateCollectionOrganizationsQuery';
import ReviewProcessSelector, {generateReviewProcessSelectorOptions} from './ReviewProcessSelector';

type ProjectOrInaccessibleGraphQL = ProjectOrganizationsQuery['projects'][number];
type TemplateCollectionOrInaccessibleGraphQL =
  TemplateCollectionOrganizationsQuery['templateCollectionsByIds'][number];

const USER_OWNED_ITEM_ERROR_MESSAGE =
  'Bulk assignment of review processes is only supported for organization-owned items.';
const DIFFERENT_ORGANIZATIONS_ERROR_MESSAGE = 'All selected items must belong to the same organization.';

export const PROJECT_ORGANIZATIONS_QUERY = gql`
  query ProjectOrganizationsQuery($projectIds: [StrictString!]!) @skipAudit @engComponent(name: NOTEBOOK) {
    projects(ids: $projectIds) {
      ... on ProjectNode {
        id
        ownerJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const TEMPLATE_COLLECTION_ORGANIZATIONS_QUERY = gql`
  query TemplateCollectionOrganizationsQuery($templateCollectionIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    templateCollectionsByIds(ids: $templateCollectionIds) {
      ... on TemplateCollectionNode {
        id
        ownerJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ORGANIZATION_REVIEW_LIFECYCLES_QUERY = gql`
  query OrganizationReviewLifecyclesQuery($organizationId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    organization(id: $organizationId) {
      ... on OrganizationNode {
        id
        reviewLifecycles {
          id
          archiveRecord {
            purpose
          }
          currentVersion {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface BulkUpdateReviewLifecycleModalProps {
  projectIds: Array<string>;
  templateCollectionIds: Array<string>;
  onRequestClose: () => void;
}

interface ReassignReviewLifecycleModalProps {
  organizationId: string;
  projectIds: Array<string>;
  templateCollectionIds: Array<string>;
  onRequestClose: () => void;
}

function ReassignReviewLifecycleModal({
  organizationId,
  projectIds,
  templateCollectionIds,
  onRequestClose,
}: ReassignReviewLifecycleModalProps): JSX.Element | null {
  const result = useSkippableBenchlingQuery<
    OrganizationReviewLifecyclesQuery,
    OrganizationReviewLifecyclesQueryVariables
  >(ORGANIZATION_REVIEW_LIFECYCLES_QUERY, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  });

  // Template collections have no legacy/auditor-based review process, so only offer the auditor option
  // when the selection is projects-only.
  const includeAuditorBasedReviewOption = templateCollectionIds.length === 0;
  // Template collections (unlike projects) can be bulk-cleared.
  // Only allowed when the selection is template-collections-only.
  const isTemplateCollectionOnly = projectIds.length === 0 && templateCollectionIds.length > 0;
  const [isAuditorBasedReview, setIsAuditorBasedReview] = useState(false);
  const [selectedReviewLifecycleIds, setSelectedReviewLifecycleIds] = useState<Array<string>>([]);

  function selectReviewLifecycleId(reviewLifecycleId: string): void {
    setSelectedReviewLifecycleIds((previouslySelected) => {
      if (!previouslySelected.includes(reviewLifecycleId)) {
        return previouslySelected.concat([reviewLifecycleId]);
      }
      return previouslySelected.filter((rlcId) => rlcId !== reviewLifecycleId);
    });
  }

  function removeReviewLifecycleId(reviewLifecycleId: string): void {
    setSelectedReviewLifecycleIds((previouslySelected) =>
      previouslySelected.filter((id) => id !== reviewLifecycleId)
    );
  }

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR || result.type === QueryResultType.SKIPPED) {
    return null;
  }

  const onRequestSubmit = async (): Promise<void> => {
    const previousBeforeUnload = window.onbeforeunload;
    window.onbeforeunload = () => '';
    try {
      const requests: Array<Promise<unknown>> = [];
      if (projectIds.length > 0) {
        requests.push(
          SourceDAO.bulkSetProjectsReviewLifecycle({
            projectApiIds: projectIds,
            reviewLifecycleApiIds: selectedReviewLifecycleIds,
          })
        );
      }
      if (templateCollectionIds.length > 0) {
        requests.push(
          TemplateCollectionDAO.bulkSetReviewLifecycle({
            templateCollectionApiIds: templateCollectionIds,
            reviewLifecycleApiIds: selectedReviewLifecycleIds,
          })
        );
      }
      await withLoadingScreen(Promise.all(requests));
      // Best-effort cache refresh; don't block success UX if refetch fails after REST succeeded.
      evictReviewLifecyclesFromCacheForCollections(templateCollectionIds);
      notifications.success('Successfully updated review process on selected items.');
      onRequestClose();
    } finally {
      window.onbeforeunload = previousBeforeUnload;
    }
  };

  const {organization} = result.data;
  if (isInaccessible(organization)) {
    assertAndContinue(false, 'User did not have access to the organization');
    return null;
  }

  const reviewProcessOptions: Array<OptionData> = generateReviewProcessSelectorOptions({
    reviewLifecycleIdAndNames: organization.reviewLifecycles
      .filter((reviewLifecycle) => reviewLifecycle.archiveRecord == null)
      .map((reviewLifecycle) => ({
        id: reviewLifecycle.id,
        name: reviewLifecycle.currentVersion.name,
      })),
    selectedReviewLifecycleIds,
    isMulti: true,
  });

  return (
    <Modal
      closeable={true}
      title="Change review process"
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Update',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled:
            selectedReviewLifecycleIds.length === 0 && !isAuditorBasedReview && !isTemplateCollectionOnly,
        },
      ]}
    >
      <Text mb="sm" {...getTestProps({component: 'BulkUpdateReviewLifecycleModal', element: 'summary'})}>
        This replaces the current review process for{' '}
        <SelectionSummary projectCount={projectIds.length} collectionCount={templateCollectionIds.length} />.
      </Text>
      <ReviewProcessSelector
        includeAuditorBasedReviewOption={includeAuditorBasedReviewOption}
        isAuditorBasedReview={isAuditorBasedReview}
        setIsAuditorBasedReview={setIsAuditorBasedReview}
        isMulti={true}
        showWarnings={false}
        selectReviewLifecycleId={selectReviewLifecycleId}
        removeReviewLifecycleId={removeReviewLifecycleId}
        options={reviewProcessOptions}
        selectedReviewLifecycleIds={selectedReviewLifecycleIds}
      />
      {isTemplateCollectionOnly && (
        <Alert variant="info" icon={<Icon icon="info-circle" />} mt="sm">
          Leave this blank to clear the review process instead of replacing it.
        </Alert>
      )}
    </Modal>
  );
}

interface SelectionSummaryProps {
  collectionCount: number;
  projectCount: number;
}

/** Bolded, pluralized description of the items the update applies to, e.g. "2 projects and 1 template collection". */
function SelectionSummary({collectionCount, projectCount}: SelectionSummaryProps): JSX.Element {
  const parts: Array<string> = [];
  if (projectCount > 0) {
    parts.push(`${projectCount} ${pluralize(projectCount, 'project')}`);
  }
  if (collectionCount > 0) {
    parts.push(`${collectionCount} ${pluralize(collectionCount, 'template collection')}`);
  }
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={part}>
          {index > 0 && ' and '}
          <Text span={true} fw="bold">
            {part}
          </Text>
        </React.Fragment>
      ))}
    </>
  );
}

interface OwnableItemValidationResult {
  errorMessage: string | null;
  organizationId: string | null;
}

function validateSharedOrganization(
  projects: Array<ProjectOrInaccessibleGraphQL>,
  templateCollections: Array<TemplateCollectionOrInaccessibleGraphQL>
): OwnableItemValidationResult {
  const organizationIds = new Set<string>();
  for (const item of [...projects, ...templateCollections]) {
    if (isInaccessible(item)) {
      assertAndContinue(false, 'User did not have access to a selected item');
      return {errorMessage: null, organizationId: null};
    }
    if (item.ownerJson.type !== 'organization') {
      return {errorMessage: USER_OWNED_ITEM_ERROR_MESSAGE, organizationId: null};
    }
    organizationIds.add(item.ownerJson.id);
  }
  if (organizationIds.size !== 1) {
    return {errorMessage: DIFFERENT_ORGANIZATIONS_ERROR_MESSAGE, organizationId: null};
  }
  return {errorMessage: null, organizationId: [...organizationIds][0]};
}

export default function BulkUpdateReviewLifecycleModal({
  projectIds,
  templateCollectionIds,
  onRequestClose,
}: BulkUpdateReviewLifecycleModalProps): JSX.Element | null {
  const projectResult = useSkippableBenchlingQuery<
    ProjectOrganizationsQuery,
    ProjectOrganizationsQueryVariables
  >(PROJECT_ORGANIZATIONS_QUERY, {
    variables: {projectIds},
    fetchPolicy: 'network-only',
    skip: projectIds.length === 0,
  });
  const templateCollectionResult = useSkippableBenchlingQuery<
    TemplateCollectionOrganizationsQuery,
    TemplateCollectionOrganizationsQueryVariables
  >(TEMPLATE_COLLECTION_ORGANIZATIONS_QUERY, {
    variables: {templateCollectionIds},
    fetchPolicy: 'network-only',
    skip: templateCollectionIds.length === 0,
  });

  let organizationId: string | null = null;
  let validationErrorMessage: string | null = null;
  let selectionIsValid = false;

  const projectsResolved =
    projectResult.type === QueryResultType.SUCCESS || projectResult.type === QueryResultType.SKIPPED;
  const templateCollectionsResolved =
    templateCollectionResult.type === QueryResultType.SUCCESS ||
    templateCollectionResult.type === QueryResultType.SKIPPED;

  if (projectsResolved && templateCollectionsResolved) {
    const projects = projectResult.type === QueryResultType.SUCCESS ? projectResult.data.projects : [];
    const templateCollections =
      templateCollectionResult.type === QueryResultType.SUCCESS
        ? templateCollectionResult.data.templateCollectionsByIds
        : [];
    const validationResult = validateSharedOrganization(projects, templateCollections);
    validationErrorMessage = validationResult.errorMessage;
    if (validationResult.organizationId != null) {
      organizationId = validationResult.organizationId;
      selectionIsValid = true;
    }
  }

  useEffect(() => {
    if (validationErrorMessage != null) {
      notifications.error(validationErrorMessage);
    }
  }, [validationErrorMessage]);

  if (
    projectResult.type === QueryResultType.PENDING ||
    templateCollectionResult.type === QueryResultType.PENDING
  ) {
    return <HorizontalThrobber />;
  } else if (
    projectResult.type === QueryResultType.ERROR ||
    templateCollectionResult.type === QueryResultType.ERROR
  ) {
    return null;
  }

  if (!selectionIsValid || organizationId == null) {
    return null;
  }

  return (
    <ReassignReviewLifecycleModal
      organizationId={organizationId}
      projectIds={projectIds}
      templateCollectionIds={templateCollectionIds}
      onRequestClose={onRequestClose}
    />
  );
}
